import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { useGridCellEditor, type CustomCellEditorProps } from 'ag-grid-react'
import { Input } from '@/components/ui/input'
import { validateWavePercent } from './model'
import type { SkuWaveRow } from './sku-model'

export function SkuPercentEditor(
  props: CustomCellEditorProps<SkuWaveRow, number | string> & {
    waveLabel: string
  }
) {
  const input = useRef<HTMLInputElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const id = useId()
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const value =
    typeof props.value === 'number'
      ? String(Number(props.value.toFixed(2)))
      : String(props.value ?? '')
  const error = validateWavePercent(value)
  useGridCellEditor({
    isCancelAfterEnd: () =>
      validateWavePercent(input.current?.value ?? value) !== null,
  })

  useEffect(() => {
    input.current?.focus()
    input.current?.select()
  }, [])
  useLayoutEffect(() => {
    const place = () => {
      const anchor = props.eGridCell.getBoundingClientRect()
      const height = panel.current?.offsetHeight ?? 160
      const width = panel.current?.offsetWidth ?? 260
      const below = anchor.bottom + 4
      const top =
        below + height <= window.innerHeight - 8
          ? below
          : anchor.top - height - 4
      setPosition({
        top: Math.max(8, Math.min(top, window.innerHeight - height - 8)),
        left: Math.max(8, Math.min(anchor.left, window.innerWidth - width - 8)),
      })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [error, props.eGridCell])

  return (
    <div
      ref={panel}
      style={position}
      className="sku-percent-editor fixed z-50 w-[260px] max-w-[calc(100vw-16px)] rounded-md border bg-card p-3 shadow-lg"
      onKeyDownCapture={event => {
        if (event.key !== 'Escape') return
        // Cancel once, before AG Grid's popup and cell handlers can both schedule a stop.
        event.preventDefault()
        event.stopPropagation()
        props.api.stopEditing(true)
        if (props.node.rowIndex !== null)
          props.api.setFocusedCell(props.node.rowIndex, props.column)
      }}
    >
      <label htmlFor={id} className="mb-2 block text-xs font-medium">
        {props.data.line?.sku} · {props.waveLabel} Wave %
      </label>
      <div className="relative">
        <Input
          id={id}
          ref={input}
          value={value}
          inputMode="decimal"
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : `${id}-hint`}
          className="h-9 pr-7 text-right tabular-nums"
          onChange={event => props.onValueChange(event.target.value)}
        />
        <span className="pointer-events-none absolute right-3 top-2 text-sm text-muted-foreground">
          %
        </span>
      </div>
      {error && (
        <p
          id={`${id}-error`}
          role="alert"
          className="mt-2 text-xs text-destructive"
        >
          {error}
        </p>
      )}
      <p id={`${id}-hint`} className="mt-2 text-xs text-muted-foreground">
        Enter to apply · Tab for next · Esc to cancel
      </p>
    </div>
  )
}
